import {
  Component,
  ElementRef,
  input,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, defer, Observable, of, Subject } from 'rxjs';
import { ConfirmPaymentInfo, EngagementsState, GetSelectedEngagementJobs, GetSelectedEngagementPrepayment } from '../../state';
import { EngagementStepComponent, EngagementStepSubmissionResult, FooterButtonType } from '../../models';
import { AbstractControl, FormControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
  ConfigService,
  ERROR_EXCLUDE_DEBIT_AND_PREPAID_PROCESSING,
  GetBusinessEntities,
  LaunchDarklyFlag,
  LaunchDarklyState,
} from '@aiwyn/config';
import { AuthAdaptorService } from '@aiwyn/auth';
import {
  AddEditAutopayDialogComponent,
  AddEditAutopayDialogData,
} from '../../../../components/main/modals/add-edit-autopay-dialog/add-edit-autopay-dialog.component';
import { LegalService } from '@aiwyn/services';
import {
  AiwynPaymentMethod,
  BankPaymentMethodType,
  BaseEngagementPaymentMethod,
  BillingScheduleEventsFixed,
  BillingScheduleEventsRecurring,
  BillingScheduleType,
  calculateInstallments,
  CardFunding,
  EngagementJobResponse,
  EngagementPaymentMethod,
  EngagementsApiService,
  EngagementStatus,
  EngagementStep,
  EngagementStepStatus,
  isPrepaymentRetryThrottled,
  isSameMethodRetryRefused,
  normalizePrepaymentRetryGuidance,
  PaymentInfoType,
  PaymentMethodBrand,
  PaymentMethodType,
  prepaymentRetryAdvancesStep,
  PrepaymentRetryGuidance,
  PrepaymentRetryResult,
  StripeApiService,
} from '@aiwyn/api';
import { AsyncPipe, CurrencyPipe, DatePipe, NgTemplateOutlet, TitleCasePipe, UpperCasePipe } from '@angular/common';
import {
  catchError,
  distinctUntilChanged,
  finalize,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap,
} from 'rxjs/operators';
import { ClearScheduledPayments, GetPaymentPlans, SettingsState } from '../../../../components/main/settings/state';
import {
  areStringsEqualIgnoreCase,
  CountryAlpha2Code,
  distinctUntilObjectChanged,
  extractFailureContext,
  FailureContext,
  getControlValueChanges,
  getLoadedResults,
  ignoreFalsy,
  ignoreNullish,
  isEmpty,
  isLoaded,
  JoinPipe,
  LoadableHelper,
  LoadableStatus,
  Logger,
  roundMonetaryValue,
} from '@aiwyn/shared';
import { LocalDateUtility } from '../../../../utility/local-date';
import { Router } from '@angular/router';
import { CommonSettingsState, GetAutoPayClients, GetClientCurrencies } from '@aiwyn/common/settings';
import {
  AbstractPaymentMethod,
  ClearArTranAllocations,
  GetPaymentFeeConfigs,
  PaymentEventBaseMetadata,
  PaymentFlow,
  PaymentInstrument,
  PaymentMethodIntent,
  PaymentMethodMode,
  PaymentPlatformService,
  PaymentsModule,
  PaymentTrackService,
  PaymentWorkflowEvent,
  PlatformCardPaymentMethod,
  resolvePrepaymentDeclineReason,
  SelectPaymentMethodComponent,
} from '@aiwyn/common/payments';
import {
  awaitingSignaturesMessage,
  derivePrepaymentStepState,
  isAwaitingSignatures,
  outstandingSignatureCount,
  PrepaymentStepState,
} from './prepayment-step-state';
import { EngagementErrorDialogComponent } from '../engagement-error-dialog/engagement-error-dialog.component';
import { PaymentMethodNoLongerRequiredStrategyModel } from '../../models/error-dialog/payment-method-no-longer-required-strategy.model';
import { MatDialog } from '@angular/material/dialog';
import { ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_REVIEW_AND_PAY } from '../../../../routing-constants';
import { toObservable } from '@angular/core/rxjs-interop';
import { AutoDraftJob, AutoDraftJobScheduleItem } from '../../models/auto-draft.models';
import { sumBy } from 'lodash';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatAccordion, MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';

/**
 * PP-3851: the two banner sentences that are NOT in the ticket's Part 2 copy table, because both terminals
 * post-date it. BOTH ARE PENDING DESIGN SIGN-OFF alongside the rest of Part 2, and are the only strings on
 * this surface that Stephen has not already reviewed.
 *
 * They live here rather than in the template because each is also the snackbar's message for its terminal,
 * and the two must not drift apart. The classified guidance sentences stay in the template, where nothing
 * else needs them.
 */
const PREPAYMENT_RETRY_REFUSED_LEAD = 'This payment method cannot be used for this payment, so it was not charged.';
const PREPAYMENT_RETRY_THROTTLED_MESSAGE = 'Too many payment attempts in a short time. Nothing has been charged. Please wait a few minutes and try again.';

@Component({
  selector: 'app-payment-info-step',
  templateUrl: './payment-info-step.component.html',
  styleUrls: ['./payment-info-step.component.scss'],
  standalone: true,
  imports: [
    NgTemplateOutlet,
    MatProgressSpinner,
    PaymentsModule,
    AsyncPipe,
    DatePipe,
    CurrencyPipe,
    TitleCasePipe,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    UpperCasePipe,
  ],
})
export class PaymentInfoStepComponent implements OnInit, OnDestroy, EngagementStepComponent {
  LoadableStatus = LoadableStatus;
  EngagementStepStatus = EngagementStepStatus;
  PaymentInfoType = PaymentInfoType;
  readonly areStringsEqualIgnoreCase = areStringsEqualIgnoreCase;
  readonly PaymentMethodType = PaymentMethodType;

  status: EngagementStepStatus;
  details = input<EngagementStep>();
  /** The engagement footer's submit-button label, so the failed-banner CTA names the same action (FIRM-11816). */
  submitButtonLabel = input<string>();
  details$ = toObservable(this.details);
  isStepComplete$ = this.details$.pipe(
    map(details => [EngagementStepStatus.COMPLETE, EngagementStepStatus.FAILED].includes(details.status)),
    shareReplay(1),
  );
  // The auto-draft layout (the "Due Now / Due Later" split and the scheduled-payment agreement) keys off the
  // step being strictly COMPLETE, matching dev — a FAILED prepayment must keep its in-progress framing rather
  // than collapse to the completed layout. Distinct from isStepComplete$, which treats FAILED as terminal for
  // the failure UX.
  isStepStrictlyComplete$ = this.details$.pipe(
    map(details => details.status === EngagementStepStatus.COMPLETE),
    shareReplay(1),
  );

  private _unsubscribe$: Subject<void> = new Subject();

  autoPayConfigsEnabled = this.configService.autoPayEnabled && this.configService.paymentsV4Enabled;

  dependenciesStatus$ = new BehaviorSubject<LoadableStatus>(LoadableStatus.DEFAULT);
  selectedEngagement$ = this.store.select(EngagementsState.getSelectedEngagement);
  autoPayData$ = new BehaviorSubject<AddEditAutopayDialogData & { isClientNonUsd: boolean }>(null);

  initAutoPay$ = this.store.dispatch([
    new GetAutoPayClients(),
    new GetPaymentPlans(),
    new GetClientCurrencies([this.store.selectSnapshot(EngagementsState.getSelectedEngagement).clientId]),
  ]).pipe(
    tap(_ => {
      this.setAutoPayData();
      this.deactivateRecurringPaymentsControl = new UntypedFormControl(
        this.autoPayData$.value?.recurringPaymentPlans.length <= 0,
        Validators.requiredTrue,
      );
    }),
  );

  applyLoadingStyles$ = this.dependenciesStatus$.pipe(
    map(status => LoadableHelper.isLoading(status)),
    tap(isLoading => {
      if (isLoading) {
        this.renderer.addClass(this.host.nativeElement, 'engagements-payment-info-loading');
      } else {
        this.renderer.removeClass(this.host.nativeElement, 'engagements-payment-info-loading');
      }
    }),
    takeWhile<boolean>(Boolean, true),
    takeUntil(this._unsubscribe$),
  );

  readonly PaymentMethodIntent = PaymentMethodIntent;
  readonly PaymentFlow = PaymentFlow;

  paymentMethodMode$ = new BehaviorSubject<PaymentMethodMode>(undefined);
  setInitialPaymentMethodMode$ = defer(() => {
    // FIRM-10915: when the engagement already has a saved method attached (re-opened picker in the
    // failed / awaiting-signatures states), preselect Saved mode so that method's section is shown as
    // actively selected and prepopulated. The id itself flows through `selectedPaymentMethodIdV5$`.
    if (this.store.selectSnapshot(EngagementsState.getSelectedEngagement).paymentMethod?.platformPaymentMethodId != null) {
      this.paymentMethodMode$.next(PaymentMethodMode.SAVED);
      return of(undefined);
    }

    return this.paymentPlatformService.getSavedPaymentMethodsExist().pipe(
      ignoreFalsy(),
      take(1),
      tap(_ => this.paymentMethodMode$.next(PaymentMethodMode.SAVED)),
    );
  });

  cardRestrictionsHint$ = this.paymentPlatformService.getCardRestrictionsHint();
  paymentMethodSubheader$ = defer(() => combineLatest([
    this.prepaymentRequired$,
    this.isAutoDraftEngagement$,
    this.cardRestrictionsHint$,
  ])).pipe(
    map(([prepaymentRequired, isAutoDraftEngagement, cardRestrictionsHint]) => {
      const paymentMethodUsageHint = [
        prepaymentRequired || isAutoDraftEngagement ? 'To be used in all payments listed below.' : '',
        !prepaymentRequired ? 'No transactions will be made at this time.' : '',
        `Credit cards may be subject to a ${this.configService.processingFeeLabel.replace(/^[A-Z]/, c => c.toLowerCase())} calculated below.`,
      ].join(' ');
      return `${paymentMethodUsageHint} ${cardRestrictionsHint ?? ''}`.trim();
    }),
  );

  selectedPaymentMethodV5$ = new BehaviorSubject<AbstractPaymentMethod>(undefined);
  // FIRM-10915: fall back to the engagement's already-selected method id until the user picks a different
  // one. The previous `startWith(...)` lost the race against this BehaviorSubject's synchronous `undefined`
  // seed (which mapped to `undefined` and clobbered the prepopulated id), so the picker never preselected it.
  selectedPaymentMethodIdV5$ = this.selectedPaymentMethodV5$.pipe(
    map(paymentMethod =>
      paymentMethod?.id
      ?? this.store.selectSnapshot(EngagementsState.getSelectedEngagement).paymentMethod?.platformPaymentMethodId),
  );

  readonly canProvidePaymentInfo$ = this.selectedEngagement$.pipe(
    map(({ status, hasPendingPaymentTaskAfterExecution }) => status !== EngagementStatus.EXECUTED || hasPendingPaymentTaskAfterExecution),
  );

  selectedPaymentMethod$ = this.selectedPaymentMethodV5$.pipe(
    switchMap(paymentMethod => {
      if (paymentMethod == undefined) {
        return of(undefined);
      }

      return this.paymentPlatformService.validatePaymentMethod(paymentMethod).pipe(
        tap(validationErrors => this.onPaymentMethodValidationChange(validationErrors)),
        map(_ => paymentMethod),
      );
    }),
    shareReplay(1),
  );

  /**
   * This may end up getting removed if payment methods made before v5 release are returned in the new endpoints.
   */
  pseudoV5PaymentMethod$ = this.details$.pipe(
    switchMap(details => {
      if ([EngagementStepStatus.COMPLETE, EngagementStepStatus.FAILED].includes(details.status)) {
        return this.selectedEngagement$.pipe(
          ignoreNullish(),
          map(({ paymentMethod }) => this._marshalPseudoV5PaymentMethod(paymentMethod)),
        );
      }

      return this.selectedPaymentMethod$.pipe(ignoreNullish());
    }),
  );

  paymentMethodValidationErrors: ValidationErrors;
  paymentMethodValidationErrorMessage: string;

  prepayment$ = this.store.select(EngagementsState.getSelectedEngagementPrepayment);
  prepaymentJobs$ = this.store.select(EngagementsState.getSelectedEngagementPrepaymentJobs);
  prepaymentRequired$ = this.store.select(EngagementsState.isSelectedEngagementPrepaymentRequired);
  prepaymentSubtotal$ = this.store.select(EngagementsState.getSelectedEngagementPrepaymentSubtotal);

  isEngagementExecuted$ = this.selectedEngagement$.pipe(
    map(({ status }) => status === EngagementStatus.EXECUTED),
  );

  didPrepaymentFail$ = this.prepayment$.pipe(
    map(prepayment => prepayment?.active === false),
  );

  /*****************************************************************************************************************************************
   *                                            Sync prepayment state machine (PP-3635 / FIRM-10915)
   ****************************************************************************************************************************************/

  /** Gates all of the new prepayment-failure UX. When off the component keeps its legacy behavior. */
  syncPrepaymentEnabled$ = this.store.select(
    LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_SYNC_ENGAGEMENT_PREPAYMENT),
  ).pipe(shareReplay(1));

  /**
   * Optimistic override for the derived step state, set by an in-place retry so the UI reflects the
   * synchronous {@link PrepaymentRetryResult} immediately (before the read-model re-fetch lands).
   * `null` means "defer to the state derived from the loaded prepayment".
   */
  private readonly prepaymentStateOverride$ = new BehaviorSubject<PrepaymentStepState | null>(null);

  /** Friendly failure copy from the latest retry attempt; falls back to the loaded prepayment's reason. */
  private readonly retryFailureMessage$ = new BehaviorSubject<string | null>(null);

  /** `retryGuidance` from the latest retry attempt; falls back to the loaded prepayment's (PP-3851 Part 2). */
  private readonly retryGuidanceOverride$ = new BehaviorSubject<string | null>(null);

  /** PP-3851 AC9: the latest attempt was refused outright on risk grounds rather than attempted and declined. */
  private readonly sameMethodRetryRefused$ = new BehaviorSubject<boolean>(false);

  /** PP-3851: the latest attempt was throttled by anduin's per-engagement retry cap, so no charge was run. */
  private readonly retryThrottled$ = new BehaviorSubject<boolean>(false);

  /**
   * A throttled attempt replaces the guidance sentence rather than the decline lead: the lead still names the
   * real reason the payment failed, which is still what the client has to fix, and the only next action is to
   * wait. Exposed for the template so this string and the snackbar's cannot drift apart.
   */
  readonly prepaymentRetryThrottledGuidance = PREPAYMENT_RETRY_THROTTLED_MESSAGE;

  /** PP-3851: drives the banner's throttle branch, which suppresses the retryGuidance sentence entirely. */
  isPrepaymentRetryThrottled$ = this.retryThrottled$.pipe(distinctUntilChanged());

  /** Drives the full-screen "Processing Payment..." overlay during an in-flight Authorize/retry. */
  private readonly paymentProcessingOverlay$ = new BehaviorSubject<boolean>(false);

  /** Latest client currency code, captured for prepayment tracking metadata. */
  private _currencyCode = 'USD';

  /** Operation-timer key for stamping `operationDurationMs` on the retry SUBMITTED → terminal triad. */
  private static readonly PREPAYMENT_RETRY_OPERATION_KEY = 'ENGAGEMENT_PREPAYMENT_RETRY';

  prepaymentStepState$: Observable<PrepaymentStepState> = combineLatest([
    this.prepayment$,
    this.syncPrepaymentEnabled$,
    this.prepaymentStateOverride$,
    this.selectedEngagement$,
  ]).pipe(
    map(([prepayment, syncEnabled, override, engagement]): PrepaymentStepState => {
      // An optimistic override (set by an in-place retry) always wins.
      if (override != null) {
        return override;
      }
      if (!syncEnabled) {
        return 'default';
      }
      // Waiting (payment confirmed, signatures still outstanding) is a pre-charge derived state, so it
      // takes precedence over the prepayment-derived states — no charge has been attempted yet.
      if (isAwaitingSignatures(engagement)) {
        return 'waiting';
      }
      return derivePrepaymentStepState(prepayment, syncEnabled, engagement);
    }),
    shareReplay(1),
  );

  /** Whether the sync flow is currently in the failed state (used to re-open the picker for retry). */
  isPrepaymentRetryable$ = this.prepaymentStepState$.pipe(
    map(state => state === 'failed'),
    distinctUntilChanged(),
  );

  /** True while the engagement is awaiting outstanding signatures with payment already confirmed. */
  isAwaitingSignatures$ = this.prepaymentStepState$.pipe(
    map(state => state === 'waiting'),
    distinctUntilChanged(),
  );

  /**
   * FIRM-10915: while awaiting signatures the picker is hidden behind a read-only pseudo-selector (the
   * engagement's stored method + a "Change payment method" trigger). This flips to `true` when the client
   * clicks that trigger, revealing the full picker. There is no path back — the client either submits the
   * swapped method or simply leaves. Recreated (reset) with the component on each step entry.
   */
  readonly isChangingPaymentMethod$ = new BehaviorSubject<boolean>(false);

  /**
   * Show the read-only "Used payment method" pseudo-selector: only while awaiting signatures (toggle-gated)
   * and before the client has chosen to change the method. Mutually exclusive with {@link showPaymentMethodPicker$}.
   */
  showWaitingPaymentMethodPseudoSelector$ = combineLatest([
    this.isAwaitingSignatures$.pipe(startWith(false)),
    this.isChangingPaymentMethod$,
  ]).pipe(
    map(([isAwaiting, isChangingPaymentMethod]) =>
      isAwaiting && this.configService.editPaymentMethodWhileAwaitingSignatures && !isChangingPaymentMethod,
    ),
  );

  /**
   * Show the "Update Payment Method" footer CTA only while awaiting signatures AND after the client has
   * opened the picker via "Change payment method" — payment info is already confirmed but the charge is
   * deferred, so the client can swap the method in place. Gating on {@link isChangingPaymentMethod$} keeps
   * the CTA hidden in the pseudo-selector view (where the picker isn't rendered, so submit would have no
   * method to validate).
   */
  showUpdatePaymentMethodButton$ = combineLatest([
    this.isAwaitingSignatures$,
    this.isChangingPaymentMethod$,
  ]).pipe(
    map(([isAwaiting, isChangingPaymentMethod]) =>
      isAwaiting && this.configService.editPaymentMethodWhileAwaitingSignatures && isChangingPaymentMethod,
    ),
    distinctUntilChanged(),
    startWith(false),
  );

  /** Pluralized Waiting banner copy (singular when one signature remains). */
  awaitingSignaturesMessage$ = this.selectedEngagement$.pipe(
    map(engagement => awaitingSignaturesMessage(outstandingSignatureCount(engagement))),
  );

  /**
   * Resolved failure message for the failed banner — design's "Payment has failed due to {reason}." form
   * (the " You may try again…" CTA is appended in the template). The reason is named for the instrument
   * the engagement's stored method uses (card vs bank account); an in-place retry overrides this via
   * {@link retryFailureMessage$} with the freshly-selected method's instrument.
   */
  prepaymentFailureMessage$ = combineLatest([
    this.prepayment$,
    this.retryFailureMessage$,
    this.selectedEngagement$,
  ]).pipe(
    map(([prepayment, retryMessage, engagement]) =>
      retryMessage ?? this.prepaymentFailureLead(prepayment?.declineCode, engagement?.paymentMethod?.type),
    ),
  );

  /**
   * PP-3851 Part 2 (ACs 7-11): anduin's classification of what the client should do next, from the latest
   * in-place attempt if there was one, else from the loaded prepayment. Same precedence as
   * {@link prepaymentFailureMessage$}, and for the same reason: the retry knows sooner than the read model.
   *
   * `null` covers both "absent" and "a value this build does not recognise", and the template renders the
   * pre-PP-3851 sentence for it (AC11), so shipping ahead of ART-8832 degrades rather than blanks.
   */
  prepaymentRetryGuidance$: Observable<PrepaymentRetryGuidance | null> = combineLatest([
    this.prepayment$,
    this.retryGuidanceOverride$,
    this.sameMethodRetryRefused$,
  ]).pipe(
    map(([prepayment, retryGuidance, refused]) =>
      // AC9: a refused attempt means the failed method cannot be charged at all, so the client has to
      // change it whatever anduin classified the original decline as (and even if it classified nothing).
      // This derives guidance FROM a refusal, never the reverse: refusal is a per-attempt risk answer and
      // is not predictable from CHANGE_METHOD, which covers plenty of non-risk declines too.
      refused ? 'CHANGE_METHOD' : normalizePrepaymentRetryGuidance(retryGuidance ?? prepayment?.retryGuidance),
    ),
    distinctUntilChanged(),
  );

  /**
   * PP-3851 Part 1: the id of the payment method whose charge most recently failed, as known client-side.
   *
   * `null` means "nothing has failed in this session, so defer to the engagement's attached method". anduin
   * binds the submitted method to the engagement at the top of every prepayment attempt, before the charge
   * and regardless of its outcome (`ExecutePrepaymentEngagementUseCase.bindRetryPaymentMethodIfProvided`),
   * so that stamp does name the method that failed. What it does not do is reach this component: only a
   * *succeeded* retry re-fetches, so after an in-place decline the local engagement still names the previous
   * method. Recording the declined method here closes that window without waiting on a round trip.
   */
  private readonly lastFailedPaymentMethodId$ = new BehaviorSubject<string | null>(null);

  /** The method that failed: the latest in-place decline if there was one, else the engagement's stamp. */
  private failedPaymentMethodId(): string | undefined {
    return this.lastFailedPaymentMethodId$.value
      ?? this.store.selectSnapshot(EngagementsState.getSelectedEngagement)?.paymentMethod?.platformPaymentMethodId;
  }

  /**
   * PP-3851 Part 1 (ACs 1-6, supersedes FIRM-11816): true when the client has staged a payment method that
   * is BOTH valid AND different from the one that failed, which is the only case where telling them the
   * method was updated is true. It was previously derived from the *event* of any validated selection
   * arriving, which made two lies reachable:
   *
   *   - re-picking the method that just failed still read as updated, because the picker's id dedupe
   *     legitimately lets a genuine change of selection through (AC3);
   *   - a second, distinct decline kept the "updated" prompt over `shareReplay(1)` and so hid its own
   *     decline reason, because nothing in the failed retry path reset it (AC4).
   *
   * Comparing against {@link failedPaymentMethodId} fixes both, and re-deriving on every emission of
   * {@link lastFailedPaymentMethodId$} is what makes a fresh decline flip it back to the decline reason.
   * `selectedPaymentMethodV5$` stays null until a *user-driven* selection (SelectPaymentMethodComponent
   * suppresses the prepopulated method), so this is false on entry and the decline reason shows first (AC1).
   */
  paymentMethodUpdatedAfterFailure$ = combineLatest([
    this.selectedPaymentMethodV5$,
    this.lastFailedPaymentMethodId$,
  ]).pipe(
    switchMap(([paymentMethod]) => {
      if (paymentMethod == null) {
        return of(false);
      }

      // Same method as the one that failed: nothing was updated, so say nothing was (AC3, AC4).
      const failedId = this.failedPaymentMethodId();
      if (failedId != null && paymentMethod.id === failedId) {
        return of(false);
      }

      return this.paymentPlatformService.validatePaymentMethod(paymentMethod).pipe(map(errors => isEmpty(errors)));
    }),
    distinctUntilChanged(),
    shareReplay(1),
  );

  /** Full-screen overlay only renders inside the sync flow. */
  showProcessingOverlay$ = combineLatest([
    this.syncPrepaymentEnabled$,
    this.paymentProcessingOverlay$,
  ]).pipe(
    map(([syncEnabled, processing]) => syncEnabled && processing),
  );

  /**
   * Show the method picker when the step is normally editable, OR (in the sync flow) whenever the
   * prepayment has failed — so the client can swap cards and retry in place without re-signing.
   */
  showPaymentMethodPicker$ = combineLatest([
    this.canProvidePaymentInfo$,
    this.isStepComplete$,
    this.isPrepaymentRetryable$.pipe(startWith(false)),
    this.isAwaitingSignatures$.pipe(startWith(false)),
    this.isChangingPaymentMethod$,
  ]).pipe(
    map(([canProvide, isStepComplete, isRetryable, isAwaiting, isChangingPaymentMethod]) =>
      (canProvide && !isStepComplete)
      || isRetryable
      // FIRM-10915: while awaiting signatures the picker stays hidden behind the read-only pseudo-selector
      // until the client clicks "Change payment method" (toggle-gated escape hatch). No cancel back.
      || (isAwaiting && this.configService.editPaymentMethodWhileAwaitingSignatures && isChangingPaymentMethod),
    ),
  );

  prepaymentMessage$ = combineLatest([
    this.prepaymentRequired$,
    this.isStepComplete$,
  ]).pipe(
    switchMap(([prepaymentRequired, isStepComplete]) => {
      const pluralization = this.store.selectSnapshot(EngagementsState.getSelectedEngagementPrepaymentJobs).length > 1 ? 's' : '';

      // when step is incomplete, only show message if prepayment is required
      if (!isStepComplete) {
        return of(
          prepaymentRequired
            ? `A Prepayment for the following service${pluralization} is required in order to execute this engagement.`
            : undefined,
        );
      }

      if (!prepaymentRequired) {
        return of('No Prepayment was required when executing this engagement.');
      }

      return combineLatest([
        this.isEngagementExecuted$,
        this.prepayment$.pipe(ignoreNullish()),
      ]).pipe(
        map(([isEngagementExecuted, prepayment]) => {
          if (!isEngagementExecuted) {
            return 'A Prepayment will be made when the engagement is executed.';
          } else if (prepayment.allocationDate == undefined) {
            return `The Prepayment for the following service${pluralization} is currently being processed.`;
          } else if (prepayment.active) {
            const allocationDate = this.datePipe.transform(prepayment.allocationDate);
            return `A Prepayment for the following service${pluralization} was made when the engagement was executed on ${allocationDate}.`;
          }

          return undefined;
        }),
      );
    }),
    ignoreNullish(),
    map(message => {
      const pluralization = this.store.selectSnapshot(EngagementsState.getSelectedEngagementJobs).length > 1 ? 's' : '';
      return `${message} Any remaining charges will be invoiced according to the billing schedule${pluralization}.`;
    }),
  );

  clientCurrency$ = this.selectedEngagement$.pipe(
    switchMap(({ clientId }) => getLoadedResults(this.store.select(CommonSettingsState.getClientCurrencyLoadable(clientId)))),
    shareReplay(1),
  );

  setSelectedBusinessEntityId$ = this.selectedEngagement$.pipe(
    map(({ businessEntityId }) => businessEntityId),
    take(1),
    ignoreNullish(),
    switchMap(businessEntityId => this.paymentPlatformService.setSelectedBusinessEntityId(businessEntityId)),
    takeUntil(this._unsubscribe$),
  );

  isAutoDraftEngagement$ = this.selectedEngagement$.pipe(
    switchMap(engagement => this.store.select(EngagementsState.getIsAutoDraftEngagement(engagement))),
  );

  engagementJobs$ = getLoadedResults(this.store.select(EngagementsState.getSelectedEngagementJobsLoadable));

  autoDraftJobs$ = this.engagementJobs$.pipe(
    map<EngagementJobResponse[], AutoDraftJob[]>(jobs => jobs.map(job => {
      const autoDraftTotal = roundMonetaryValue(job.total - job.prepaymentAmount);
      return {
        id: job.id,
        name: job.name,
        total: autoDraftTotal,
        billingScheduleType: job.billingSchedule.type,
        schedule: this._getBillingScheduleEvents(job.billingSchedule.events, job.billingSchedule.type, autoDraftTotal),
      };
    })),
    shareReplay(1),
  );

  autoDraftRange$ = this.engagementJobs$.pipe(
    // start date to end date ('Aug 2025 - Dec 2026')
    map(jobs => {
      if (isEmpty(jobs)) {
        return '';
      }

      const allFixedScheduleFirstDates = jobs
        .filter(job => job.billingSchedule.type === BillingScheduleType.FIXED)
        .map(job => new Date((job.billingSchedule.events as BillingScheduleEventsFixed).billList[0].billDate).getTime());
      const allRecurringScheduleStartDates = jobs
        .filter(job => job.billingSchedule.type === BillingScheduleType.RECURRING)
        .map(job => new Date((job.billingSchedule.events as BillingScheduleEventsRecurring).startDate).getTime());
      const earliestStartDate = Math.min(...allFixedScheduleFirstDates.concat(allRecurringScheduleStartDates));

      const allFixedScheduleLastDates = jobs
        .filter(job => job.billingSchedule.type === BillingScheduleType.FIXED)
        .map(job => new Date((job.billingSchedule.events as BillingScheduleEventsFixed).billList.slice(-1)[0].billDate).getTime());
      const allRecurringScheduleEndDates = jobs
        .filter(job => job.billingSchedule.type === BillingScheduleType.RECURRING)
        .map(job => new Date((job.billingSchedule.events as BillingScheduleEventsRecurring).endDate).getTime());
      const latestEndDate = Math.max(...allFixedScheduleLastDates.concat(allRecurringScheduleEndDates));

      return `${this.datePipe.transform(earliestStartDate, 'MMM yyyy')} - ${this.datePipe.transform(latestEndDate, 'MMM yyyy')}`;
    }),
  );

  autoDraftInstallmentsCount$ = this.autoDraftJobs$.pipe(
    map(jobs => sumBy(jobs, job => sumBy(job.schedule, schedule => schedule.installmentsCount))),
  );

  autoDraftTotal$ = this.autoDraftJobs$.pipe(
    map(jobs => roundMonetaryValue(sumBy(jobs, 'total'))),
  );

  constructor(
    private readonly store: Store,
    public readonly configService: ConfigService,
    public readonly authAdaptor: AuthAdaptorService,
    public readonly router: Router,
    private readonly host: ElementRef<PaymentInfoStepComponent & HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly logger: Logger,
    // autopay dialog
    public readonly legalService: LegalService,
    private readonly dialog: MatDialog,
    private readonly datePipe: DatePipe,
    private readonly titleCasePipe: TitleCasePipe,
    // v5
    public readonly paymentPlatformService: PaymentPlatformService,
    private readonly stripeApiService: StripeApiService,
    // FIRM-10915 sync prepayment failure UX
    private readonly engagementsApiService: EngagementsApiService,
    private readonly paymentTrackService: PaymentTrackService,
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
  ) {
    if (this.configService.setupIntentByBusinessEntity) {
      this.setSelectedBusinessEntityId$.subscribe();
    }
  }

  ngOnInit(): void {
    const engagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    this.store.dispatch(new GetSelectedEngagementJobs(engagement.id));
    if (
      engagement.status === EngagementStatus.EXECUTED // async (success or failure), sync (success only, failure stays in SENT)
      || ['PROCESSING', 'FAILED'].includes(engagement.prepaymentStatus) // no FF check because this field and the status persists when going back to async
      // PP-3742: a sync-flow failure leaves the engagement in SENT, and the hub does not always carry prepaymentStatus for it —
      // fetch for SENT engagements that require payment info so the failed-prepayment alert can render. Harmless when no
      // prepayment was attempted: the query returns undefined and the step stays in its default state.
      || (engagement.status === EngagementStatus.SENT && engagement.paymentInfoType === PaymentInfoType.REQUIRED)
    ) {
      this.store.dispatch(new GetSelectedEngagementPrepayment(engagement.id));
    }

    this.applyLoadingStyles$.subscribe();

    this.dependenciesStatus$.asObservable().pipe(
      isLoaded(),
      take(1),
      tap(_ => this.initialize()),
      takeUntil(this._unsubscribe$),
    ).subscribe();

    this.store.selectOnce(
      EngagementsState.getEngagementStepPendingChanges(
        this.store.selectSnapshot(EngagementsState.getSelectedEngagementId),
        this.details().type,
      ),
    ).pipe(
      tap(({ enableAutoPay, dayOfTheMonth, maxAmount }) => {
        if (enableAutoPay != null) {
          this.enableAutoPayControl.setValue(enableAutoPay);
        }

        if (dayOfTheMonth != null) {
          this.dayControl.setValue(dayOfTheMonth, { emitEvent: false });
        }

        if (maxAmount != null) {
          this.maxAmountControl.setValue(maxAmount, { emitEvent: false });
        }
      }),
      take(1),
      takeUntil(this._unsubscribe$),
    ).subscribe();

    this.setInitialPaymentMethodMode$.subscribe();

    // PP-3635: drive the document-root processing overlay off the same signal the inline overlay used.
    this.showProcessingOverlay$.pipe(
      distinctUntilChanged(),
      takeUntil(this._unsubscribe$),
    ).subscribe(show => (show ? this.openProcessingOverlay() : this.closeProcessingOverlay()));
  }

  /**
   * PP-3635: render the full-screen "Processing Payment..." overlay through the CDK overlay so it attaches at
   * the document root (above the engagement sidebar). An in-component fixed/absolute element cannot escape the
   * mat-drawer-content stacking context (the drawer sits in a higher context and uses a transform), so it
   * would always paint behind the sidebar. The template is embedded via this component's ViewContainerRef, so
   * its nodes keep this component's encapsulation and the top-level overlay styles still apply.
   */
  private openProcessingOverlay(): void {
    if (this.processingOverlayRef || !this.processingOverlayTemplate) {
      return;
    }

    this.processingOverlayRef = this.overlay.create({
      positionStrategy: this.overlay.position().global(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
    });
    this.processingOverlayRef.attach(new TemplatePortal(this.processingOverlayTemplate, this.viewContainerRef));
  }

  private closeProcessingOverlay(): void {
    this.processingOverlayRef?.dispose();
    this.processingOverlayRef = null;
  }

  getFooterButtons(): Observable<FooterButtonType[]> {
    this.dependenciesStatus$.next(LoadableStatus.LOADING);

    return combineLatest([
      this.autoPayConfigsEnabled ? this.initAutoPay$ : of(undefined),
      // TODO: Since we don't have real time payments for prepayments, the payment summary component has 'ignoreLateFees' to just hide them.
      // this.store.dispatch(new SetPayLateFees(false)),
      this.paymentPlatformService.getSavedPaymentMethodsExist().pipe(
        ignoreNullish(),
        tap(savedPaymentMethodsExist => this.savedPaymentMethodsExist = savedPaymentMethodsExist),
      ),
      this.store.dispatch([
        new GetPaymentFeeConfigs(),
        new GetBusinessEntities(defer(() => this.stripeApiService.getBusinessEntities())),
      ]),
    ]).pipe(
      tap(_ => this.dependenciesStatus$.next(LoadableStatus.LOADED)),
      switchMap(_ => this.showUpdatePaymentMethodButton$),
      map(showUpdatePaymentMethodButton =>
        showUpdatePaymentMethodButton ? [FooterButtonType.UPDATE_PAYMENT_METHOD] : [],
      ),
    );
  }

  setAutoPayData(): void {
    const selectedEngagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    const autoPayDisplay = this.store.selectSnapshot(CommonSettingsState.getAutoPayClients)
      .find(({ clientId }) => clientId === selectedEngagement.clientId);

    if (autoPayDisplay == null) {
      return;
    }

    const recurringPaymentPlans = this.store.selectSnapshot(SettingsState.getPaymentPlans)
      .filter(paymentPlanUi => paymentPlanUi.client.id === autoPayDisplay.clientId)
      .map(({ paymentPlan }) => paymentPlan);

    combineLatest([
      this.paymentPlatformService.getSavedPaymentMethods().pipe(
        map(paymentMethods => paymentMethods.some(({ id }) => id === autoPayDisplay.method?.paymentMethodId)),
      ),
      getLoadedResults(this.store.select(CommonSettingsState.getClientCurrencyLoadable(autoPayDisplay.clientId))),
    ]).pipe(
      take(1),
      tap(([isPaymentMethodFromCurrentUser, clientCurrency]) => this.autoPayData$.next({
        isEnabled: autoPayDisplay.active,
        account: {
          id: autoPayDisplay.clientId,
          name: autoPayDisplay.account,
        },
        didCreate: !autoPayDisplay.active || isPaymentMethodFromCurrentUser,
        autoPay: autoPayDisplay.autoPayId ? autoPayDisplay : undefined,
        recurringPaymentPlans,
        isClientNonUsd: clientCurrency?.code !== 'USD',
      })),
    ).subscribe();
  }

  submissionTriggered: boolean;
  submissionProcessing: boolean;

  shouldCreateEngagementPaymentMethod(): boolean {
    return this.paymentMethodMode$.value !== PaymentMethodMode.SET_UP_LATER;
  }

  savedPaymentMethodsExist: boolean;
  @ViewChildren(SelectPaymentMethodComponent) selectPaymentMethodComponent: QueryList<SelectPaymentMethodComponent>;

  // PP-3635: the processing overlay is rendered through the CDK overlay (document-root level) so it covers
  // the whole viewport — including the engagement sidebar — instead of being trapped inside the
  // mat-drawer-content stacking context. See openProcessingOverlay().
  @ViewChild('processingOverlayTemplate') private readonly processingOverlayTemplate: TemplateRef<unknown>;
  private processingOverlayRef: OverlayRef | null = null;

  submit(): Observable<EngagementStepSubmissionResult> {
    this.submissionTriggered = true;
    this.submissionProcessing = true;
    // Full-screen overlay covers the in-flight Authorize (rendered only when the sync flag is on).
    this.paymentProcessingOverlay$.next(true);

    // FIRM-10915: in the failed prepayment state the footer CTA ("Try Again") retries the charge in
    // place — no re-confirm, no route-out — instead of running the normal payment-info submission.
    if (this.isPrepaymentRetryable()) {
      return this.submitPrepaymentRetry();
    }

    if (
      this.autoPayData$.value?.isEnabled
      && !this.store.selectSnapshot(EngagementsState.isSelectedEngagementPrepaymentRequired)
      && !this.store.selectSnapshot(EngagementsState.isSelectedEngagementAutoDraft)
    ) {
      return this.store.dispatch(new ConfirmPaymentInfo()).pipe(
        map(_ => ({ success: true })),
        tap(() => this._trackPaymentInfoConfirmed()),
        catchError(_ => of({ success: false, errorMessage: 'Failed to confirm payment information.' })),
        finalize(() => {
          this.submissionProcessing = false;
          this.paymentProcessingOverlay$.next(false);
        }),
      );
    }

    return this.selectPaymentMethodComponent.first.validate().pipe(
      map(isPaymentMethodSelectionValid => this.getErrorSnackbarMessage(isPaymentMethodSelectionValid)),
      map(errorMessage => ({ success: !errorMessage, errorMessage })),
      // TODO: Confirm with Team Rocket if inline autopay creation is still desired.
      // switchMap(result => {
      //   if (!result.success || !this.enableAutoPayControl.value) {
      //     return of(result);
      //   }
      //
      //   return this.saveAutoPayPlan();
      // }),
      switchMap(result => {
        if (!result.success) {
          return of(result);
        }

        /**
         * If this is a new card or an existing payment method, we need to persist the payment method.
         */
        const engagementPaymentMethod: EngagementPaymentMethod = this.shouldCreateEngagementPaymentMethod()
          ? this.createEngagementPaymentMethod(this.selectedPaymentMethodV5$.value)
          : null;

        return this.store.dispatch(new ConfirmPaymentInfo(engagementPaymentMethod)).pipe(
          map(_ => ({ success: true })),
          tap(() => this._trackPaymentInfoConfirmed()),
        );
      }),
      catchError(error => {
        this.logger.error('ENGAGEMENT : PaymentInfoStepComponent : submit : ', error);

        const errorMessage: string = error.message || '';
        if (errorMessage === 'The Payment Info Type \'NONE\' is not allowed.') {
          const engagementId = this.store.selectSnapshot(EngagementsState.getSelectedEngagementId);
          const hasAdditionalTasks = this.store.selectSnapshot(EngagementsState.getSelectedEngagementSteps).length > 1;
          return this.dialog.open(EngagementErrorDialogComponent, {
            data: { concreteStrategy: new PaymentMethodNoLongerRequiredStrategyModel(hasAdditionalTasks, engagementId) },
          }).afterClosed().pipe(
            switchMap(_ => {
              return of({ success: false, errorMessage });
            }),
          )
        }

        return of({ success: false, errorMessage: 'Failed to save payment information.' });}
      ),
      finalize(() => {
        this.submissionProcessing = false;
        this.paymentProcessingOverlay$.next(false);
      }),
    );
  }

  createEngagementPaymentMethod(selectedPaymentMethod: AbstractPaymentMethod): EngagementPaymentMethod {
    const baseEngagementPaymentMethod: Pick<
      EngagementPaymentMethod,
      'id' | 'platformPaymentMethodId' | 'type' | 'last4' | 'cardType' | 'country'
    > = {
      id: null,
      platformPaymentMethodId: selectedPaymentMethod.id,
      type: selectedPaymentMethod.type,
      last4: selectedPaymentMethod.lastFour,
      cardType: selectedPaymentMethod.brand,
      country: selectedPaymentMethod.country,
    };

    if ('expirationYear' in selectedPaymentMethod) {
      return {
        ...baseEngagementPaymentMethod,
        type: selectedPaymentMethod.subType,
        state: selectedPaymentMethod.billingDetails.address.state,
        zip: selectedPaymentMethod.billingDetails.address.postalCode,
        expirationDt: LocalDateUtility.generateLocalDateFormattedValue(
          (selectedPaymentMethod as PlatformCardPaymentMethod).expirationYear,
          (selectedPaymentMethod as PlatformCardPaymentMethod).expirationMonth,
        ),
      };
    }

    return {
      ...baseEngagementPaymentMethod,
      state: null,
      zip: null,
      expirationDt: null,
    };
  }

  getErrorSnackbarMessage(isPaymentMethodSelectionValid: boolean): string {
    const errorMessages: string[] = [];

    // payment method
    if (!isPaymentMethodSelectionValid) {
      errorMessages.push(`add${this.savedPaymentMethodsExist ? ' or select' : ''} a valid payment method`);
    }

    // autopay
    if (this.enableAutoPayControl.value && !this.isAutoPayGroupValid()) {
      errorMessages.push('complete the AutoPay form');
    }

    if (errorMessages.length === 0) {
      return undefined;
    } else if (errorMessages.length > 3) {
      return 'Unable to submit the form. Please complete all required fields.';
    }

    return `Please ${JoinPipe.staticTransform(errorMessages)}.`;
  }

  navigateToReviewAndPayEngagementPrepayment(arTranId: number): void {
    this.store.dispatch(new ClearArTranAllocations());
    this.router.navigate(
      [ROUTE_TOKEN_MAIN_BASE, ROUTE_TOKEN_REVIEW_AND_PAY],
      { queryParams: { invoiceId: arTranId } },
    );
  }

  /*****************************************************************************************************************************************
   *                                          Sync prepayment retry (PP-3635 / FIRM-10915 AC1)
   ****************************************************************************************************************************************/

  /**
   * Whether the step is currently in the failed prepayment state, i.e. the footer CTA should retry the
   * charge in place. Mirrors {@link prepaymentStepState$} (optimistic override wins, else derived).
   */
  private isPrepaymentRetryable(): boolean {
    const override = this.prepaymentStateOverride$.value;
    if (override != null) {
      return override === 'failed';
    }

    const prepayment = this.store.selectSnapshot(EngagementsState.getSelectedEngagementPrepayment);
    const syncEnabled = this.store.selectSnapshot(
      LaunchDarklyState.getFeatureFlag(LaunchDarklyFlag.ENABLE_SYNC_ENGAGEMENT_PREPAYMENT),
    );
    const engagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    return derivePrepaymentStepState(prepayment, syncEnabled, engagement) === 'failed';
  }

  /**
   * Retry a failed prepayment in place — no route-out, no re-signing of the engagement letter. The
   * method picker (re-opened by the failed state) lets the client swap cards first; the picker already
   * attaches a freshly entered card to the customer via its setup intent, so the only remaining step is
   * re-running the charge. Driven by the footer "Try Again" button via {@link submit}.
   *
   * Result → step-submission mapping: `succeeded` completes the step; `processing` (ACH/bank debit
   * accepted) is also treated as success so the step advances with the "you'll be notified" messaging;
   * `failed` and `refused` both keep the user on the step, the first surfacing the decline and the second
   * saying the charge was never attempted (PP-3851 AC9).
   */
  private submitPrepaymentRetry(): Observable<EngagementStepSubmissionResult> {
    const engagementId = this.store.selectSnapshot(EngagementsState.getSelectedEngagementId);
    const selectedPaymentMethod = this.selectedPaymentMethodV5$.value;

    // Clear anything carried from a previous attempt so a stale reason, classification or refusal cannot
    // outlive the attempt that produced it. Each is repopulated by _handlePrepaymentRetryResult.
    this.retryFailureMessage$.next(null);
    this.retryGuidanceOverride$.next(null);
    this.sameMethodRetryRefused$.next(false);
    this.retryThrottled$.next(false);
    this._trackPrepaymentSubmitted(selectedPaymentMethod);

    // Pass the picker's selected platform payment-method id (Stripe `pm_…`) so the retry binds a freshly
    // swapped card (AC1). The picker already attaches it to the customer via its setup intent, so this is
    // belt-and-suspenders for the swap case; the backend falls back to the engagement's method when absent.
    return this.engagementsApiService.processEngagementPrepayment(engagementId, selectedPaymentMethod?.id).pipe(
      tap(result => this._handlePrepaymentRetryResult(result, selectedPaymentMethod, engagementId)),
      // A non-advancing terminal reuses the banner's copy (resolved by the tap above) so the snackbar names
      // the same reason rather than falling back to the container's generic step-failure message, matching
      // the catchError branch below. Which terminals advance is an ALLOW-LIST in the model, so a status this
      // build does not know about keeps the client on the step instead of telling them they have paid.
      map<PrepaymentRetryResult, EngagementStepSubmissionResult>(result =>
        prepaymentRetryAdvancesStep(result)
          ? { success: true }
          // A throttle is the one terminal whose snackbar differs from the banner lead. The lead keeps naming
          // the prior decline, but "nothing happened, wait" is the only thing the client cannot read off the
          // banner, so the snackbar says that instead.
          : {
            success: false,
            errorMessage: isPrepaymentRetryThrottled(result) ? PREPAYMENT_RETRY_THROTTLED_MESSAGE : this.retryFailureMessage$.value,
          }),
      catchError(error => {
        this.logger.error('ENGAGEMENT : PaymentInfoStepComponent : submitPrepaymentRetry : ', error);
        this._trackPrepaymentFailed(selectedPaymentMethod, extractFailureContext(error));
        this.prepaymentStateOverride$.next('failed');
        this.retryFailureMessage$.next(this.prepaymentFailureLead(undefined, this._retryMethodType(selectedPaymentMethod)));
        return of<EngagementStepSubmissionResult>({ success: false, errorMessage: 'We were unable to process your payment. Please try again.' });
      }),
      finalize(() => {
        this.submissionProcessing = false;
        this.paymentProcessingOverlay$.next(false);
      }),
      take(1),
      takeUntil(this._unsubscribe$),
    );
  }

  private _handlePrepaymentRetryResult(
    result: PrepaymentRetryResult,
    selectedPaymentMethod: AbstractPaymentMethod,
    engagementId: number,
  ): void {
    // Refusal and throttle are each their own terminal: neither attempted a charge, so neither can be
    // reported as a decline, and neither may advance the step. Both are tested ahead of the switch so the
    // `default` below stays a pure catch-all for a status this build has never seen.
    if (isSameMethodRetryRefused(result)) {
      this._handleSameMethodRetryRefused(result, selectedPaymentMethod);
      return;
    }
    if (isPrepaymentRetryThrottled(result)) {
      this._handlePrepaymentRetryThrottled(result, selectedPaymentMethod);
      return;
    }

    switch (result.status) {
      case 'succeeded':
        this._trackPrepaymentSucceeded(selectedPaymentMethod);
        this.prepaymentStateOverride$.next('completed');
        // Refresh the read model so the persisted (cleared) state survives a reload.
        this.store.dispatch(new GetSelectedEngagementPrepayment(engagementId, false));
        break;
      case 'processing':
        // ACH/bank-debit accepted — treat acceptance as the success terminal for the charge submission.
        this._trackPrepaymentSucceeded(selectedPaymentMethod);
        this.prepaymentStateOverride$.next('processing');
        break;
      // 'failed', plus any terminal this build has never seen. Succeeded, processing, refused and throttled
      // are all handled above, so everything reaching here is a decline or an unknown, and both render as an
      // ordinary decline and (per prepaymentRetryAdvancesStep) leave the step where it is. Wrong copy is
      // recoverable; wrongly telling a client they have paid is not.
      default: {
        // Resolve a stable, client-safe display message from the decline code, never the raw Stripe reason.
        const failureLead = this.prepaymentFailureLead(result.declineCode, this._retryMethodType(selectedPaymentMethod));
        this._trackPrepaymentFailed(selectedPaymentMethod, {
          errorCategory: 'CLIENT_ERROR',
          errorCode: result.declineCode,
          errorMessage: failureLead,
        }, result.declineCode);
        this.prepaymentStateOverride$.next('failed');
        this.retryFailureMessage$.next(failureLead);
        this.retryGuidanceOverride$.next(result.retryGuidance ?? null);
        // PP-3851 Part 1 (AC4): this attempt's method is now the one that failed, so the banner drops the
        // "payment method updated" prompt and shows the reason THIS method was declined. `null` when the
        // client submitted nothing, in which case anduin charged the engagement's stamped method and
        // failedPaymentMethodId() already resolves to it.
        this.lastFailedPaymentMethodId$.next(selectedPaymentMethod?.id ?? null);
        break;
      }
    }
  }

  /**
   * PP-3851 AC9: anduin declined to even attempt the charge, because the method that failed carries a risk
   * code. Rendered distinctly from an ordinary decline: the lead says the charge was never run, rather than
   * naming a reason for a charge that did not happen, and the guidance steers to a different method.
   *
   * This lead is the one string on this banner that is NOT in the PP-3851 copy table, because the refusal
   * case post-dates it. Pending sign-off with the rest of Part 2.
   */
  private _handleSameMethodRetryRefused(result: PrepaymentRetryResult, selectedPaymentMethod: AbstractPaymentMethod): void {
    const refusalLead = PREPAYMENT_RETRY_REFUSED_LEAD;
    this._trackPrepaymentFailed(selectedPaymentMethod, {
      errorCategory: 'CLIENT_ERROR',
      errorCode: result.declineCode,
      errorMessage: refusalLead,
    }, result.declineCode);
    this.prepaymentStateOverride$.next('failed');
    this.retryFailureMessage$.next(refusalLead);
    this.retryGuidanceOverride$.next(result.retryGuidance ?? null);
    this.sameMethodRetryRefused$.next(true);
    this.lastFailedPaymentMethodId$.next(selectedPaymentMethod?.id ?? null);
  }

  /**
   * PP-3851: anduin throttled this attempt (5 per 900 seconds per engagement) and ran no charge. It arrives
   * as an ordinary 200 with a typed payload, so nothing upstream of the status check notices it, and before
   * this was handled it fell through to the success branch and told a throttled client their payment had
   * gone through.
   *
   * Unlike a refusal, the lead keeps naming the PRIOR decline: `declineCode` here describes the failure the
   * banner is already rendering rather than a fresh charge, that reason is still what the client has to fix,
   * and the only thing the throttle changes is that the next action is to wait. So the throttle message
   * replaces the guidance sentence instead, and the snackbar carries it too, since "nothing happened" is the
   * one thing the client cannot see from the banner alone.
   */
  private _handlePrepaymentRetryThrottled(result: PrepaymentRetryResult, selectedPaymentMethod: AbstractPaymentMethod): void {
    this._trackPrepaymentFailed(selectedPaymentMethod, {
      errorCategory: 'CLIENT_ERROR',
      errorCode: 'RATE_LIMITED',
      errorMessage: PREPAYMENT_RETRY_THROTTLED_MESSAGE,
    }, result.declineCode);
    this.prepaymentStateOverride$.next('failed');
    this.retryFailureMessage$.next(this.prepaymentFailureLead(result.declineCode, this._retryMethodType(selectedPaymentMethod)));
    this.retryGuidanceOverride$.next(result.retryGuidance ?? null);
    this.retryThrottled$.next(true);
    // Deliberately NOT recorded as the failed method: no charge was attempted, so the method that failed is
    // still whatever failed last. Recording it would suppress the "payment method updated" prompt for a
    // client who had legitimately staged a different card and merely hit the cap.
  }

  /** Build the design banner lead — "Payment has failed due to {reason}." — naming the instrument used. */
  private prepaymentFailureLead(declineCode: string | undefined, methodType: string | undefined): string {
    return `Payment has failed due to ${resolvePrepaymentDeclineReason(declineCode, this.paymentInstrument(methodType))}.`;
  }

  /** Map a payment-method type (card funding or ACH/ACSS/BACS) to the instrument noun used in copy. */
  private paymentInstrument(methodType: string | undefined): PaymentInstrument {
    if (!methodType) {
      return 'payment method';
    }
    return AbstractPaymentMethod.isDirectDebit(methodType as PaymentMethodType) ? 'bank account' : 'card';
  }

  /** The retried method's type — the freshly-selected method if the client swapped, else the stored one. */
  private _retryMethodType(selectedPaymentMethod: AbstractPaymentMethod | undefined): string | undefined {
    return selectedPaymentMethod?.type
      ?? this.store.selectSnapshot(EngagementsState.getSelectedEngagement).paymentMethod?.type;
  }

  private _buildPrepaymentEventBase(selectedPaymentMethod?: AbstractPaymentMethod): PaymentEventBaseMetadata {
    return {
      paymentType: 'invoice',
      paymentMethodType: selectedPaymentMethod
        ? (selectedPaymentMethod.subType ?? selectedPaymentMethod.type ?? '').toString().toUpperCase()
        : undefined,
      currency: this._currencyCode,
      surface: 'engagement',
    };
  }

  private _trackPrepaymentSubmitted(selectedPaymentMethod?: AbstractPaymentMethod): void {
    const subtotal = this.store.selectSnapshot(EngagementsState.getSelectedEngagementPrepaymentSubtotal);
    this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_CHARGE_SUBMITTED, {
      ...this._buildPrepaymentEventBase(selectedPaymentMethod),
      amount: subtotal,
      chargeIndex: 0,
      subtotal,
      processingFeeAmount: 0,
      internationalFeeAmount: 0,
    });
    this.paymentTrackService.startOperation(PaymentInfoStepComponent.PREPAYMENT_RETRY_OPERATION_KEY);
  }

  private _trackPrepaymentSucceeded(selectedPaymentMethod?: AbstractPaymentMethod): void {
    this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_CHARGE_SUCCEEDED, {
      ...this._buildPrepaymentEventBase(selectedPaymentMethod),
      amount: this.store.selectSnapshot(EngagementsState.getSelectedEngagementPrepaymentSubtotal),
      chargeIndex: 0,
      operationDurationMs: this.paymentTrackService.endOperation(PaymentInfoStepComponent.PREPAYMENT_RETRY_OPERATION_KEY),
    });
  }

  private _trackPrepaymentFailed(
    selectedPaymentMethod: AbstractPaymentMethod | undefined,
    failureContext: FailureContext,
    stripeDeclineCode?: string,
  ): void {
    this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_CHARGE_FAILED, {
      ...this._buildPrepaymentEventBase(selectedPaymentMethod),
      ...failureContext,
      chargeIndex: 0,
      operationDurationMs: this.paymentTrackService.endOperation(PaymentInfoStepComponent.PREPAYMENT_RETRY_OPERATION_KEY),
      stripeDeclineCode,
    });
  }

  /**
   * FIRM-10915 (PP-3635): emit the single PAYMENT_INFO_CONFIRMED user event when the payment info step is
   * confirmed. The boolean metadata describes the backend reality — `prepaymentCharged: false` means the
   * charge is deferred behind outstanding signatures (the engagement entered the Waiting state). The async
   * charge terminal is a backend event (ART-8104 prepayment.*.async), never emitted client-side.
   */
  private _trackPaymentInfoConfirmed(): void {
    const engagement = this.store.selectSnapshot(EngagementsState.getSelectedEngagement);
    const prepaymentRequired = this.store.selectSnapshot(EngagementsState.isSelectedEngagementPrepaymentRequired);
    const paymentMethodAssigned = this.selectedPaymentMethodV5$.value != null
      || engagement.paymentMethod?.platformPaymentMethodId != null;

    this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_INFO_CONFIRMED, {
      ...this._buildPrepaymentEventBase(this.selectedPaymentMethodV5$.value),
      confirmationMade: true,
      paymentMethodAssigned,
      // Charges now only when no signatures are outstanding; otherwise the charge is deferred (Waiting)
      // and fires server-side once the final signature lands.
      prepaymentCharged: prepaymentRequired && outstandingSignatureCount(engagement) === 0,
    });
  }

  /*****************************************************************************************************************************************
   *                                                             Save Card Stuff
   ****************************************************************************************************************************************/


  onPaymentMethodValidationChange(validationErrors: ValidationErrors): void {
    this.paymentMethodValidationErrors = validationErrors;
    this.paymentMethodValidationErrorMessage = this.getPriorityPaymentMethodErrorMessage(validationErrors);
  }

  getPriorityPaymentMethodErrorMessage(validationErrors: ValidationErrors): string {
    if (Object.keys(validationErrors ?? {}).length === 0) {
      return null;
    }

    if ('funding' in validationErrors) {
      return ERROR_EXCLUDE_DEBIT_AND_PREPAID_PROCESSING;
    } else if ('brand' in validationErrors) {
      return 'Sorry, we do not accept American Express. Please use a different payment method.';
    } else if ('expiration' in validationErrors) {
      return 'Card expired';
    } else if ('country' in validationErrors) {
      return 'Payments must be made using a card with a United States billing address.';
    } else if ('state' in validationErrors) {
      const restrictedStates = JoinPipe.staticTransform(this.store.selectSnapshot(CommonSettingsState.getRestrictedUsStates)
        .map(({ stateAbbreviation }) => stateAbbreviation), 'or');
      return `Sorry, we do not accept credit cards from ${restrictedStates}. Please use a different payment method.`;
    }

    return null;
  }

  /*****************************************************************************************************************************************
   *                                                              AutoPay Stuff
   ****************************************************************************************************************************************/

  private _didUpdatePaymentMethod$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Form Inputs
   */
  deactivateRecurringPaymentsControl: FormControl<boolean>;
  enableAutoPayControl = new FormControl(false, Validators.requiredTrue);
  dayControl: UntypedFormControl = new UntypedFormControl(this.configService.defaultFirmBillingDay || 1, Validators.required);
  maxAmountControl: UntypedFormControl = new UntypedFormControl(undefined);
  autoPayGroup: UntypedFormGroup = new UntypedFormGroup(
    {
      day: this.dayControl,
      maxAmount: this.maxAmountControl,
    },
    { updateOn: 'blur' },
  );

  // TODO: Confirm with Team Rocket if inline autopay creation is still desired.
  // dayOptions: { value: number | 'last', display: string }[] = [
  //   ...Array
  //     .from({ length: 28 })
  //     .map((_, index) => ({ value: index + 1, display: moment.localeData().ordinal(index + 1) })),
  //   { value: 'last', display: 'Last' },
  // ];

  paymentMethodMonitor$: Observable<unknown> = this.selectedPaymentMethod$.pipe(
    ignoreNullish(),
    distinctUntilChanged(),
    tap(_ => this._didUpdatePaymentMethod$.next(true)),
    takeUntil(this._unsubscribe$),
  );

  /**
   * Disable / enable the form based off the enable toggle
   */
  shouldDisableForm$: Observable<boolean> = combineLatest([
    this._didUpdatePaymentMethod$,
    getControlValueChanges(this.enableAutoPayControl).pipe(
      switchMap(enabled => {
        if (!enabled) {
          return of(false);
        }

        // a client cannot have recurring payments and auto-pay at the same time
        const clientId = this.store.selectSnapshot(EngagementsState.getSelectedEngagement).clientId;
        const paymentPlans = this.store.selectSnapshot(SettingsState.getPaymentPlans);
        const clientHasActivePaymentPlan = paymentPlans.find(({ client }) => client.id === clientId)?.paymentPlan.active;

        return clientHasActivePaymentPlan ? getControlValueChanges(this.deactivateRecurringPaymentsControl) : of(true);
      }),
    ),
  ]).pipe(
    distinctUntilObjectChanged(),
    map(([didUpdatePaymentMethod, areRecurringPaymentsValid]) =>
      (this.autoPayData$.value?.didCreate == null && !didUpdatePaymentMethod) || !areRecurringPaymentsValid,
    ),
    takeUntil(this._unsubscribe$),
  );

  doDisableForm$: Observable<boolean> = this.shouldDisableForm$.pipe(
    tap(shouldDisableForm => this.updateAutopayGroupEnabledState(!shouldDisableForm)),
    takeUntil(this._unsubscribe$),
  );

  initialize(): void {
    this.doDisableForm$.subscribe();
    this.paymentMethodMonitor$.subscribe();

    this.clientCurrency$.pipe(
      ignoreNullish(),
      tap(currency => this._currencyCode = currency.code ?? 'USD'),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  ngOnDestroy(): void {
    // TODO :: Re-evaluate these steps. This was not in circulation for AGES.

    // const paymentInfoStep = this.store.selectSnapshot(EngagementsState.getSelectedEngagementSteps)
    //   .find(({ type }) => type === EngagementStepType.PAYMENT);
    //
    // if (paymentInfoStep.status !== EngagementStepStatus.COMPLETE && this.autoPayData$.value?.isEnabled === false) {
    //   this.store.dispatch(
    //     new SetEngagementStepPendingChanges(
    //       this.store.selectSnapshot(EngagementsState.getSelectedEngagementId),
    //       this.details.type,
    //       {
    //         enableAutoPay: this.enableAutoPayControl.value,
    //         dayOfTheMonth: this.dayControl.value,
    //         maxAmount: this.maxAmountControl.value,
    //       },
    //     ),
    //   );
    // }
    //
    // this.cardService.isCardInfoGroupRequired = true;
    // this.savedPaymentMethodDropdownService.preserveSelectedPaymentMethodOnInit = false;

    this.closeProcessingOverlay();
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  updateAutopayGroupEnabledState(enabled: boolean): void {
    if (enabled) {
      this.autoPayGroup.enable({ emitEvent: false });
    } else {
      this.autoPayGroup.disable({ emitEvent: false });
    }
  }

  // TODO: Confirm with Team Rocket if inline autopay creation is still desired.
  /*saveAutoPayPlan(): Observable<EngagementStepSubmissionResult> {
    return this.saveAutoPayPlanV5().pipe(
      map(_ => ({ success: true })),
      catchError(error => {
        this.logger.error('ENGAGEMENT : PaymentInfoStepComponent : saveAutoPayPlan : ', error);
        return of({
          success: false,
          errorMessage: `Failed to ${this.autoPayData$.value.autoPay?.autoPayId == undefined ? 'create' : 'update'} AutoPay plan.`,
        });
      }),
      switchMap((result: EngagementStepSubmissionResult) => {
        if (!result.success) {
          return of(result);
        }

        const actions = [new GetAutoPayClients(true), new ClearScheduledPayments()]
          .concat(this.autoPayData$.value?.recurringPaymentPlans.length > 0 ? new GetPaymentPlans(true) : []);

        return this.store.dispatch(actions).pipe(
          map(_ => ({ success: true })),
          tap(_ => this.setAutoPayData()),
          catchError(_ => of({ success: true, errorMessage: 'Saving succeeded, but failed to get details.' })),
        );
      }),
      map((result: EngagementStepSubmissionResult) => !result.success ? result : { success: true }),
      catchError(_ => of({ success: false, errorMessage: 'Failed to deactivate existing recurring payment plans.' })),
      takeUntil(this._unsubscribe$),
    );
  }

  saveAutoPayPlanV5(): Observable<unknown> {
    return AddEditAutopayDialogComponent.deactivateRecurringPaymentPlans(
      this.autoPayData$.value.recurringPaymentPlans,
      this.paymentPlanApiService.deactivatePaymentPlan.bind(this.paymentPlanApiService),
    ).pipe(
      // build save payload
      switchMap(_ => this.selectedPaymentMethodV5$),
      map(selectedPaymentMethod => {
        const startDate = AddEditAutopayDialogComponent.getStartDate(
          this.autoPayData$.value.autoPay?.startDate,
          this.dayControl.value,
          this.configService.autopayBufferDays,
        );
        const formattedStartDate = this.datePipe.transform(startDate, 'yyyy-MM-dd');

        const recipientEmails = [this.authStateAdaptor.getContactSnapshot()?.username];
        return AddEditAutopayDialogComponent.getSaveAutoPayPlanPayload(
          this.autoPayData$.value,
          selectedPaymentMethod,
          formattedStartDate,
          recipientEmails,
          this.maxAmountControl.value,
          this.authAdaptor.isAdmin,
          this.authAdaptor.isAdmin ? this.authAdaptor.getStaffId : this.authAdaptor.getContactIdOrNull,
        );
      }),
      // execute save
      switchMap(saveAutoPayPlanPayload =>
        this.autoPayData$.value.autoPay?.autoPayId
          ? this.paymentPlanApiService.updateAutoPaymentPlan(saveAutoPayPlanPayload as UpdateAutoPaymentPlanRequest)
          : this.paymentPlanApiService.createAutoPaymentPlan(saveAutoPayPlanPayload as CreateAutoPaymentPlanRequest),
      ),
    );
  }*/

  manageAutoPay(): void {
    this.dialog.open(AddEditAutopayDialogComponent, { data: this.autoPayData$.value }).afterClosed().pipe(
      switchMap(didUpdate => {
        if (!didUpdate) {
          return of(null);
        }

        const didDisableRecurringPaymentPlans = this.autoPayData$.value?.recurringPaymentPlans.length > 0;
        const actions = [new GetAutoPayClients(true), new ClearScheduledPayments()]
          .concat(didDisableRecurringPaymentPlans ? new GetPaymentPlans(true) : []);

        return this.store.dispatch(actions).pipe(
          tap(_ => this.setAutoPayData()),
        );
      }),
      take(1),
      takeUntil(this._unsubscribe$),
    ).subscribe();
  }

  isAutoPayGroupValid(): boolean {
    const formControls: AbstractControl[] = [
      this.autoPayGroup,
      ...(!isEmpty(this.autoPayData$.value?.recurringPaymentPlans) ? [this.deactivateRecurringPaymentsControl] : []),
    ];

    formControls.forEach(formControl => {
      formControl.markAllAsTouched();
      formControl.markAsDirty();
    });

    return formControls.every(formControl => formControl.status === 'VALID');
  }

  private _marshalPseudoV5PaymentMethod(paymentMethod: BaseEngagementPaymentMethod): AbstractPaymentMethod {
    if (paymentMethod == undefined) {
      return undefined;
    }

    const pseudoV5PaymentMethodModel: AiwynPaymentMethod = {
      platform: undefined,
      id: paymentMethod.platformPaymentMethodId,
      name: undefined,
      last4: paymentMethod.last4,
      billingDetails: {
        address: {
          city: undefined,
          country: paymentMethod.country,
          line1: undefined,
          line2: undefined,
          postalCode: undefined,
          state: paymentMethod.state,
        },
        name: undefined,
      },
      country: paymentMethod.country as CountryAlpha2Code,
      ...(
        [BankPaymentMethodType.ACH, BankPaymentMethodType.ACSS, BankPaymentMethodType.BACS]
          .some(type => areStringsEqualIgnoreCase(type, paymentMethod.type))
          ? {
            brand: undefined,
            paymentMethodType: paymentMethod.type.toLowerCase() as BankPaymentMethodType,
          }
          : {
            brand: paymentMethod.brand as PaymentMethodBrand,
            paymentMethodType: paymentMethod.type.toLowerCase() as CardFunding,
            expirationMonth: undefined,
            expirationYear: undefined,
          }
      ),
    };

    return AbstractPaymentMethod.create(pseudoV5PaymentMethodModel, undefined);
  }

  private _getBillingScheduleEvents(
    events: BillingScheduleEventsFixed | BillingScheduleEventsRecurring,
    billingScheduleType: BillingScheduleType,
    jobTotalMinusPrepayment: number,
  ): AutoDraftJobScheduleItem[] {
    if (billingScheduleType === BillingScheduleType.FIXED) {
      return (events as BillingScheduleEventsFixed).billList.map(event => ({
        label: this.datePipe.transform(event.billDate),
        installmentsCount: 1,
        amountPerInstallment: event.amount,
      }));
    }

    const recurringEvents = events as BillingScheduleEventsRecurring;
    const installmentsCount = calculateInstallments(recurringEvents.startDate, recurringEvents.endDate, recurringEvents.frequency);
    return [
      {
        label: `${this.titleCasePipe.transform(recurringEvents.frequency)} starting ${this.datePipe.transform(recurringEvents.startDate)}`,
        installmentsCount,
        amountPerInstallment: roundMonetaryValue(jobTotalMinusPrepayment / installmentsCount),
      },
    ];
  }
}
